import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import Loading from '@aurora/shared-client/components/common/Loading/Loading';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import type { AppContextInterface } from '@aurora/shared-client/components/context/AppContext/AppContext';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { Article, Message } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  ContentWorkflowState,
  RepliesFormat
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { ContextMessageFragment } from '@aurora/shared-generated/types/graphql-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useContext, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { contextMessageToMessageView } from '../../../../helpers/messages/MessageHelper/MessageHelper';
import type { ItemType, MessageViewVariant } from '../../../../types/enums';
import type {
  ContextMessageQuery,
  ContextMessageQueryVariables,
  MessageParentFragment,
  MessageRepliesCountFragment
} from '../../../../types/graphql-types';
import messageQuery from '../../../context/ContextMessage.query.graphql';
import { UrlObject, useContextObjectRefFromUrl } from '../../../context/useContextObjectFromUrl';
import type { ItemViewTypeAndProps } from '../../../entities/types';
import useRepliesProperties from '../../../messages/LinearReplyList/useRepliesProperties';
import MessageDeepLink from '../../../messages/MessageDeepLink/MessageDeepLink';
import MessageReplyCallToAction from '../../../messages/MessageReplyCallToAction/MessageReplyCallToAction';
import MessageView from '../../../messages/MessageView/MessageView';
import { ContributorsListType } from '../../../messages/types';
import { useAutoScrollToReplies } from '../../../messages/useAutoScrollToReplies';
import { useIsPreviewMode } from '../../../messages/useCurrentOrPreviewMessage';
import useInView from '../../../useInView';
import localStyles from './TkbArticle.module.pcss';
import useLinkProperties from '../../../common/ExternalLinkWarningModal/useLinkProperties';
import type { MessageViewStandardProps } from '../../../messages/MessageView/types';

const ThreadedReplyList = dynamic(
  () => import('../../../messages/ThreadedReplyList/ThreadedReplyList')
);

const LinearReplyList = dynamic(() => import('../../../messages/LinearReplyList/LinearReplyList'));

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * List variant to use for the collection.
   */
  listVariant: ListVariantTypeAndProps<Message>;
  /**
   * The variant display style used when rendering the view component.
   */
  viewVariant: ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>;
  /**
   * The style of pager to use.
   */
  pagerVariant: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams>;
  /**
   * The number of items to display per page, also used to determine
   * the number of items used for first page even when pagination is not
   * used.
   */
  pageSize: number;
  /**
   * If true, only fetch reply data during the client-side render,
   * not during the server-side render.  Default is false.
   */
  lazyFetchReplies?: boolean;
  /**
   * If true, display the bottom navigation section. Default is false.
   */
  showNavigationPanel?: boolean;
  /**
   * Show published or last updated date
   */
  showTimestamp: boolean;
  /**
   * Show version number
   */
  showVersion: boolean;
  /**
   * Show helpfulness
   */
  showHelpfulness: boolean;
  /**
   * Contributor list type
   */
  contributorListType: ContributorsListType;
}

/**
 * TKB Article.
 * @constructor
 * @author Amit Agrawal
 */
const TkbArticle: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  viewVariant,
  listVariant,
  showNavigationPanel,
  pagerVariant,
  pageSize,
  lazyFetchReplies,
  showTimestamp,
  showVersion,
  showHelpfulness,
  contributorListType
}) => {
  const cx = useClassNameMapper(localStyles);
  const { contextMessage: contextMessageData } = useContext(AppContext);
  const isPreviewMode = useIsPreviewMode();
  const [inViewRef, inView] = useInView<HTMLDivElement>();
  const { id: commentId } = useContextObjectRefFromUrl(UrlObject.REPLY);
  const showDeepLinkedComment = commentId !== null;
  const { isCrawler } = useContext<AppContextInterface>(AppContext);
  const { router } = useEndUserRoutes();
  const articleIdParam = router.getUnwrappedQueryParam(EndUserQueryParams.ARTICLE_ID);
  const useBio = contributorListType === ContributorsListType.PANEL;
  const showContributorList = contributorListType === ContributorsListType.LINK;
  const ref = useRef<HTMLDivElement>(null);
  const autoScrollToReplies = useAutoScrollToReplies();

  const queryResult = useQueryWithTracing<ContextMessageQuery, ContextMessageQueryVariables>(
    module,
    messageQuery,
    {
      variables: {
        id: articleIdParam
      },
      skip: !articleIdParam
    }
  );
  const contextMessage = queryResult?.data?.message ?? contextMessageData;

  const { repliesFormat, repliesFormatLoading } = useRepliesProperties(contextMessage?.board);
  const { isExternalLinkWarningEnabled, linkPropertiesLoading } = useLinkProperties(
    contextMessage?.board?.id
  );

  if (queryResult.loading || repliesFormatLoading || linkPropertiesLoading) {
    return (
      <div style={{ height: `${ref.current?.offsetHeight}px` }}>
        <Loading />
      </div>
    );
  }

  const isThreadedRepliesFormat = repliesFormat === RepliesFormat.Threaded;
  (viewVariant.props as MessageViewStandardProps).useExternalLinkWarning =
    isExternalLinkWarningEnabled;

  const topicViewVariant: ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant> = {
    type: viewVariant.type,
    props: {
      ...viewVariant.props,
      useSubject: true,
      useSubjectLink: false,
      subjectClassName: cx('text-body lia-g-mb-20'),
      subjectAs: 'h1',
      useHeader: false,
      useRevision: true,
      className: cx(`${viewVariant?.props?.className ?? ''} lia-article`),
      avatarSize: IconSize.PX_50,
      actionMenuItems: null,
      useAccordionForReply: false,
      useReplies: false,
      useRepliesCount: false,
      useTextBody: true,
      usePreview: showDeepLinkedComment,
      usePreviewMedia: true,
      clampBodyLines: showDeepLinkedComment ? 2 : null,
      showTimestamp,
      showVersion,
      useBio,
      showContributorList,
      useTkbHelpfulnessRating: showHelpfulness,
      useGuideNavigation: showNavigationPanel
    }
  };

  const decoratedViewVariant = { ...viewVariant };
  decoratedViewVariant.props.useSubject = false;
  decoratedViewVariant.props.useIntroduction = false;
  decoratedViewVariant.props.useRepliesCount =
    decoratedViewVariant.props.useRepliesCount && isThreadedRepliesFormat;
  decoratedViewVariant.props.useAccordionForReply =
    decoratedViewVariant.props.useAccordionForReply && isThreadedRepliesFormat;
  decoratedViewVariant.props.repliesFormat = repliesFormat;

  const { contentWorkflow } = contextMessage as Article;
  const isMessagePreviewAndDraft =
    contentWorkflow?.state !== ContentWorkflowState.Publish && isPreviewMode;
  const showComments = !isMessagePreviewAndDraft;

  return (
    <>
      <div className={cx(className, 'lia-message-body-container')} ref={ref}>
        <MessageView
          entity={contextMessageToMessageView(contextMessage)}
          variant={topicViewVariant}
        />
      </div>
      <div ref={inViewRef}>
        {showDeepLinkedComment ? (
          <>
            <MessageDeepLink rootMessage={contextMessage} viewVariant={viewVariant} />
            <MessageReplyCallToAction message={contextMessage} />
          </>
        ) : (
          showComments && (
            <>
              {!isCrawler &&
              !autoScrollToReplies &&
              lazyFetchReplies &&
              !inView ? null : repliesFormat === RepliesFormat.Threaded ? (
                <ThreadedReplyList
                  message={
                    contextMessage as ContextMessageFragment &
                      MessageRepliesCountFragment &
                      MessageParentFragment
                  }
                  listVariant={listVariant}
                  viewVariant={decoratedViewVariant}
                  pagerVariant={pagerVariant}
                  pageSize={pageSize}
                  useEmpty
                  editorRef={ref}
                />
              ) : (
                <LinearReplyList
                  message={
                    contextMessage as ContextMessageFragment &
                      MessageRepliesCountFragment &
                      MessageParentFragment
                  }
                  viewVariant={decoratedViewVariant}
                  listVariant={listVariant}
                  pagerVariant={pagerVariant}
                  useEmpty
                  editorRef={ref}
                />
              )}
              <MessageReplyCallToAction
                message={contextMessage}
                className={cx({
                  'lia-reply-cta-wrapper':
                    contextMessage?.repliesCount > 0 &&
                    viewVariant.props.useAccordionForReply &&
                    isThreadedRepliesFormat
                })}
              />
            </>
          )
        )}
      </div>
    </>
  );
};

export default TkbArticle;
